import InputField from "components/atoms/InputField";
import { BreadcrumbNav } from "components/molecules";
import React, { useEffect, useState } from "react";
import {
  FeedbackSampleOption,
  PageMode,
  PCRResultOptions,
  SampleSendType,
  YesAndNoOptions,
} from "utils/constant";
import { hasSentType, isPcrNeeded } from "./sampleTypeRules";
import { useFormik } from "formik";
import InputDate from "components/atoms/InputDate";
import moment from "moment";
import InputSelect from "components/atoms/InputSelect";
import { formatParasite } from "utils/parasite";
import { useSelector } from "react-redux";
import Button from "components/atoms/Button";
import {
  errorToastNotif,
  getDataFromLocalStorage,
  nullConverter,
  successToastNotif,
} from "utils/common";
import Skl5 from "services/SurveilansKnowlesiLab5";

const Skl5Service = new Skl5();

const { SAD, DBS, WB } = SampleSendType;

// Konversi null menjadi string kosong agar React controlled component tidak warning
const normalizeInitialValues = (data) => {
  const result = {};
  for (const key of Object.keys(data)) {
    result[key] = data[key] === null ? "" : data[key];
  }
  return result;
};

const FormComponent = ({ changeMode, pageMode }) => {
  const { currentUser } = useSelector((state) => state.authReducer);
  const [selectedData, setSelectedData] = useState(() => {
    const data = getDataFromLocalStorage("SELECTED_ITEM");
    return JSON.parse(data);
  });
  // Blok jenis sampel hanya dirender untuk jenis yang dikirim (data lama: SAD).
  const hasSad = hasSentType(selectedData, SAD);
  const hasDbs = hasSentType(selectedData, DBS);
  const hasWb = hasSentType(selectedData, WB);
  const [isPcr, setIsPcr] = useState(isPcrNeeded(selectedData));
  const formik = useFormik({
    initialValues: normalizeInitialValues(selectedData),
    validateOnMount: true,

    validate: (values) => {
      const errors = {};
      if (!values.sample_received_date) {
        errors.sample_received_date = "Tanggal sampel diterima harus diisi";
      }
      if (isPcrNeeded(values)) {
        if (!values.pcr_finish_checking_date) {
          errors.pcr_finish_checking_date =
            "Tanggal selesai pemeriksaan PCR harus diisi";
        }
        if (
          !values.pcr_checking_result ||
          values.pcr_checking_result.length === 0
        ) {
          errors.pcr_checking_result = "Hasil pemeriksaan PCR harus diisi";
        }
      }
      return errors;
    },

    onSubmit: async (values) => {
      if (isPcrNeeded(values)) {
        const missingFields = [];
        if (!values.pcr_finish_checking_date) {
          missingFields.push("Tanggal selesai pemeriksaan PCR");
        }
        if (
          !values.pcr_checking_result ||
          values.pcr_checking_result.length === 0
        ) {
          missingFields.push("Hasil Pemeriksaan PCR");
        }
        if (missingFields.length > 0) {
          errorToastNotif(`Step 2 harus diisi: ${missingFields.join(", ")}`);
          return;
        }
      }

      try {
        await Skl5Service.update(nullConverter(values), selectedData.id).then(
          () => {
            changeMode(PageMode.view);
            successToastNotif("Surveilans Knowlesi Lab Berhasil Diperbarui");
          }
        );
      } catch (error) {
        errorToastNotif(error);
      }
    },
  });

  // Helper: set field value + touch agar Formik re-validate
  const setField = (name, value) => {
    formik.setFieldValue(name, value);
    formik.setFieldTouched(name, true, false);
  };

  const handleConditionChange = (field, value) => {
    const nextIsPcr = isPcrNeeded({
      ...selectedData,
      ...formik.values,
      [field]: value,
    });
    setIsPcr(nextIsPcr);

    // Reset Step 2 hanya bila PCR tidak lagi dibutuhkan, agar data yang sudah
    // diisi tidak hilang saat kondisi jenis lain diubah.
    if (!nextIsPcr) {
      formik.setFieldValue("pcr_finish_checking_date", "");
      formik.setFieldValue("pcr_checking_result", []);
      formik.setFieldTouched("pcr_finish_checking_date", true, false);
      formik.setFieldTouched("pcr_checking_result", true, false);
    }
  };

  return (
    <>
      <BreadcrumbNav
        items={[
          {
            label: "Knowlesi Lab",
            onClick: () => changeMode(PageMode.view),
          },
          {
            label: "Edit Data Knowlesi Lab",
          },
        ]}
      />

      <div className="informasi-wilayah">
        <section className="list-item">
          <p className="item-label">Provinsi</p>
          <p className="item-value">{selectedData?.province_name}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Kab/Kota</p>
          <p className="item-value">{selectedData?.district_name}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Faskes Pencatat</p>
          <p className="item-value">{selectedData?.fasyankes_name}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Kode Slide</p>
          <p className="item-value">{selectedData?.slide_code}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Nama pasien</p>
          <p className="item-value">{selectedData?.patient_name}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Alamat Domisili Pasien</p>
          <p className="item-value">{selectedData?.address}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Pekerjaan pasien</p>
          <p className="item-value">{selectedData?.job}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Jenis Parasit</p>
          <p className="item-value">{formatParasite(selectedData?.parasyte)}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Lab Pengirim Sampel</p>
          <p className="item-value">
            {
              (
                selectedData?.source_lab_id || selectedData?.source_fasyankes_id
              )?.split("|")[1]
            }
          </p>
        </section>
        <section className="list-item">
          <p className="item-label">Tanggal Sampel dikirim</p>
          <p className="item-value">{selectedData?.sample_send_date}</p>
        </section>
      </div>

      <div className="form-grouping">
        <p
          className="warning"
          style={{ marginTop: "24px", marginBottom: "24px" }}
        >
          Step 1
        </p>
        <div className="row">
          <div className="col-sm-6">
            <InputDate
              label={"Tanggal sampel diterima"}
              name={"sample_received_date"}
              placeholder={"Pilih Tanggal sampel diterima"}
              dateFormat={"YYYY-MM-DD"}
              onChange={(e) => {
                const selectedDate = e
                  ? moment(e).format("YYYY-MM-DD")
                  : "";
                setField("sample_received_date", selectedDate);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.sample_received_date}
              error={
                formik.errors.sample_received_date &&
                formik.touched.sample_received_date
              }
              errorMessage={formik.errors.sample_received_date}
              isRequired={true}
              isValidDate={(currentDate) =>
                !selectedData?.sample_send_date ||
                moment(currentDate).isSameOrAfter(
                  moment(selectedData.sample_send_date)
                )
              }
            />
          </div>
        </div>

        {hasSad && (
        <div className="row">
          <div className="col-sm-6">
            <InputSelect
              label={
                "Kondisi Sampel Sediaan Apusan Darah (SAD) (Memenuhi syarat pemeriksaan PCR)"
              }
              name={"sample_sad_condition"}
              placeholder={"Pilih Kondisi sampel"}
              data={YesAndNoOptions}
              onChange={(e) => {
                const selectedValue = e ? e.value : "";
                handleConditionChange("sample_sad_condition", selectedValue);
                setField("sample_sad_condition", selectedValue);
              }}
              onBlur={formik.handleBlur}
              value={YesAndNoOptions.find(
                (item) => item.value === formik.values.sample_sad_condition
              )}
              error={
                formik.errors.sample_sad_condition &&
                formik.touched.sample_sad_condition
              }
              errorMessage={formik.errors.sample_sad_condition}
              isRequired={true}
              isClearable={false}
            />
          </div>
          <div className="col-sm-6">
            <InputSelect
              label={"Umpan Balik Kondisi Sampel Sediaan Apusan Darah (SAD)"}
              name={"sample_sad_feedback"}
              placeholder={"Umpan Balik Kondisi Sampel"}
              data={FeedbackSampleOption}
              onChange={(e) => {
                const selectedValue = e ? e.value : "";
                setField("sample_sad_feedback", selectedValue);
              }}
              onBlur={formik.handleBlur}
              value={FeedbackSampleOption.find(
                (item) => item.value === formik.values.sample_sad_feedback
              )}
              error={
                formik.errors.sample_sad_feedback &&
                formik.touched.sample_sad_feedback
              }
              errorMessage={formik.errors.sample_sad_feedback}
              isRequired={false}
              isClearable={false}
            />
          </div>
        </div>
        )}

        {hasDbs && (
        <div className="row">
          <div className="col-sm-6">
            <InputSelect
              label={
                "Kondisi Sampel Dried Blood Spot (DBS) (Memenuhi syarat pemeriksaan PCR)"
              }
              name={"sample_dbs_condition"}
              placeholder={"Pilih Kondisi sampel"}
              data={YesAndNoOptions}
              onChange={(e) => {
                const selectedValue = e ? e.value : "";
                handleConditionChange("sample_dbs_condition", selectedValue);
                setField("sample_dbs_condition", selectedValue);
              }}
              onBlur={formik.handleBlur}
              value={YesAndNoOptions.find(
                (item) => item.value === formik.values.sample_dbs_condition
              )}
              error={
                formik.errors.sample_dbs_condition &&
                formik.touched.sample_dbs_condition
              }
              errorMessage={formik.errors.sample_dbs_condition}
              isRequired={true}
              isClearable={false}
            />
          </div>
          <div className="col-sm-6">
            <InputField
              label={"Umpan Balik Kondisi Sampel Dried Blood Spot (DBS)"}
              name={"sample_dbs_feedback"}
              type={"text"}
              placeholder={"Umpan Balik Kondisi Sampel"}
              value={formik.values.sample_dbs_feedback}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.errors.sample_dbs_feedback &&
                formik.touched.sample_dbs_feedback
              }
              errorMessage={formik.errors.sample_dbs_feedback}
              isRequired={false}
            />
          </div>
        </div>
        )}

        {hasWb && (
        <div className="row">
          <div className="col-sm-6">
            <InputSelect
              label={
                "Kondisi Sampel Whole Blood EDTA (WB) (Memenuhi syarat pemeriksaan PCR)"
              }
              name={"sample_wb_condition"}
              placeholder={"Pilih Kondisi sampel"}
              data={YesAndNoOptions}
              onChange={(e) => {
                const selectedValue = e ? e.value : "";
                handleConditionChange("sample_wb_condition", selectedValue);
                setField("sample_wb_condition", selectedValue);
              }}
              onBlur={formik.handleBlur}
              value={YesAndNoOptions.find(
                (item) => item.value === formik.values.sample_wb_condition
              )}
              error={
                formik.errors.sample_wb_condition &&
                formik.touched.sample_wb_condition
              }
              errorMessage={formik.errors.sample_wb_condition}
              isRequired={true}
              isClearable={false}
            />
          </div>
          <div className="col-sm-6">
            <InputField
              label={"Umpan Balik Kondisi Sampel Whole Blood EDTA (WB)"}
              name={"sample_wb_feedback"}
              type={"text"}
              placeholder={"Umpan Balik Kondisi Sampel"}
              value={formik.values.sample_wb_feedback}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.errors.sample_wb_feedback &&
                formik.touched.sample_wb_feedback
              }
              errorMessage={formik.errors.sample_wb_feedback}
              isRequired={false}
            />
          </div>
        </div>
        )}

        {isPcr && (
          <>
            <p
              className="warning"
              style={{ marginTop: "24px", marginBottom: "24px" }}
            >
              Step 2
            </p>
            <div className="row">
              <div className="col-sm-6">
                <InputDate
                  label={"Tanggal selesai pemeriksaan PCR"}
                  name={"pcr_finish_checking_date"}
                  placeholder={"Pilih Tanggal selesai pemeriksaan PCR"}
                  dateFormat={"YYYY-MM-DD"}
                  onChange={(e) => {
                    const selectedDate = e
                      ? moment(e).format("YYYY-MM-DD")
                      : "";
                    setField("pcr_finish_checking_date", selectedDate);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.pcr_finish_checking_date}
                  error={
                    formik.errors.pcr_finish_checking_date &&
                    formik.touched.pcr_finish_checking_date
                  }
                  errorMessage={formik.errors.pcr_finish_checking_date}
                  isRequired={isPcr}
                />
              </div>
              <div className="col-sm-6">
                <InputSelect
                  label={"Hasil Pemeriksaan PCR"}
                  name={"pcr_checking_result"}
                  placeholder={"Pilih Hasil Pemeriksaan PCR"}
                  data={PCRResultOptions}
                  italicizeParasite
                  isMultipleSelect
                  onChange={(selected) => {
                    const selectedValues = Array.isArray(selected)
                      ? selected.map((item) => item.value)
                      : [];
                    setField("pcr_checking_result", selectedValues);
                  }}
                  onBlur={formik.handleBlur}
                  value={PCRResultOptions.filter((item) =>
                    (formik.values.pcr_checking_result || []).includes(
                      item.value
                    )
                  )}
                  error={
                    formik.errors.pcr_checking_result &&
                    formik.touched.pcr_checking_result
                  }
                  errorMessage={formik.errors.pcr_checking_result}
                  isRequired={isPcr}
                />
              </div>
            </div>
          </>
        )}
        <div className="form_action">
          <Button
            variant={"tertiary"}
            size={"normal"}
            onClick={() => changeMode(PageMode.view)}
          >
            Batalkan
          </Button>
          <Button
            variant={"primary"}
            size={"normal"}
            onClick={formik.handleSubmit}
            isDisabled={!formik.isValid}
          >
            Simpan Data
          </Button>
        </div>
      </div>
    </>
  );
};

export default FormComponent;
